import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  HomeIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const MonthlyView = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [attendanceData, setAttendanceData] = useState([]);
  const [monthStats, setMonthStats] = useState({});

  useEffect(() => {
    // Sample monthly attendance data
    const generateMonthlyData = () => {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const firstDay = new Date(year, month, 1).getDay();
      
      const data = [];
      const workTypes = ['office', 'wfh', 'hybrid'];
      
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month, day);
        const dayOfWeek = date.getDay();
        
        let status, workType, checkIn, checkOut, hours;
        
        if (dayOfWeek === 0 || dayOfWeek === 6) {
          status = 'weekend';
          workType = null;
        } else if (day === 15) {
          status = 'holiday';
          workType = null;
        } else if (day === 5 || day === 12) {
          status = 'leave';
          workType = null;
        } else if (day === 18) {
          status = 'absent';
          workType = null;
        } else {
          const rand = Math.random();
          if (rand < 0.1) status = 'late';
          else if (rand < 0.15) status = 'half_day';
          else status = 'present';
          
          workType = workTypes[Math.floor(Math.random() * workTypes.length)];
          
          if (status === 'late') {
            checkIn = '09:' + (30 + Math.floor(Math.random() * 30)).toString().padStart(2, '0') + ' AM';
          } else {
            checkIn = '09:' + Math.floor(Math.random() * 20).toString().padStart(2, '0') + ' AM';
          }
          
          if (status === 'half_day') {
            checkOut = '01:30 PM';
            hours = 4.5;
          } else {
            const outHour = 17 + Math.floor(Math.random() * 3);
            const outMin = Math.floor(Math.random() * 60);
            checkOut = (outHour > 12 ? outHour - 12 : outHour) + ':' + outMin.toString().padStart(2, '0') + ' PM';
            hours = 8 + Math.random() * 2;
          }
        }
        
        data.push({
          date: day,
          fullDate: date,
          status,
          workType,
          checkIn,
          checkOut,
          hours: hours ? Math.round(hours * 10) / 10 : 0,
          notes: status === 'holiday' ? 'Independence Day' : 
                 status === 'leave' ? 'Annual Leave' :
                 status === 'late' ? 'Traffic delay' : null
        });
      }
      
      return { data, firstDay };
    };

    const { data, firstDay } = generateMonthlyData();
    
    // Calculate statistics
    const workingDays = data.filter(d => !['weekend', 'holiday'].includes(d.status));
    const presentDays = data.filter(d => ['present', 'late', 'half_day'].includes(d.status));
    const absentDays = data.filter(d => ['absent', 'leave'].includes(d.status));
    const totalHours = data.reduce((sum, d) => sum + (d.hours || 0), 0);
    
    const stats = {
      totalDays: data.length,
      workingDays: workingDays.length,
      presentDays: presentDays.length,
      absentDays: absentDays.length,
      attendance: workingDays.length > 0 ? Math.round((presentDays.length / workingDays.length) * 100) : 0,
      totalHours: Math.round(totalHours * 10) / 10,
      averageHours: presentDays.length > 0 ? Math.round((totalHours / presentDays.length) * 10) / 10 : 0,
      firstDay
    };

    setTimeout(() => {
      setAttendanceData(data);
      setMonthStats(stats);
      setLoading(false);
    }, 1000);
  }, [currentDate]);

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'absent':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'late':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'half_day':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'leave':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'holiday':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'weekend':
        return 'bg-gray-100 text-gray-600 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'present':
        return <CheckCircleIcon className="w-4 h-4 text-green-600" />;
      case 'absent':
        return <XCircleIcon className="w-4 h-4 text-red-600" />;
      case 'late':
        return <ExclamationTriangleIcon className="w-4 h-4 text-yellow-600" />;
      case 'half_day':
        return <ClockIcon className="w-4 h-4 text-orange-600" />;
      case 'leave':
        return <CalendarDaysIcon className="w-4 h-4 text-blue-600" />;
      case 'holiday':
        return <CalendarDaysIcon className="w-4 h-4 text-purple-600" />;
      case 'weekend':
        return <HomeIcon className="w-4 h-4 text-gray-500" />;
      default:
        return <CheckCircleIcon className="w-4 h-4 text-gray-500" />;
    }
  };

  const getWorkTypeIcon = (workType) => {
    switch (workType) {
      case 'office':
        return <BuildingOfficeIcon className="w-3 h-3 text-blue-600" />;
      case 'wfh':
        return <HomeIcon className="w-3 h-3 text-green-600" />;
      case 'hybrid':
        return <div className="w-3 h-3 bg-purple-600 rounded-full"></div>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-apollo-gray">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-apollo-primary mx-auto mb-4"></div>
            <p className="text-gray-600">Loading monthly attendance...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />
      
      {/* Page Header */}
      <div className="bg-gradient-to-r from-apollo-secondary to-apollo-accent text-white py-6 sm:py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center mb-3 sm:mb-4">
              <CalendarDaysIcon className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 opacity-80" />
            </div>
            <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-2 sm:mb-4">Monthly Attendance View</h1>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl opacity-90 max-w-2xl mx-auto px-4">
              Detailed calendar view of your attendance records and work patterns
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation */}
        <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3 lg:gap-4 justify-center lg:justify-start">
          <button
            onClick={() => navigate('/attendance')}
            className="bg-white border border-gray-300 text-gray-700 px-3 sm:px-4 lg:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl hover:bg-gray-50 transition-colors font-medium text-xs sm:text-sm lg:text-base"
          >
            ← Back to Dashboard
          </button>
          <button
            onClick={() => navigate('/attendance/history')}
            className="bg-white border border-gray-300 text-gray-700 px-3 sm:px-4 lg:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl hover:bg-gray-50 transition-colors font-medium text-xs sm:text-sm lg:text-base"
          >
            Attendance History
          </button>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6 sm:gap-8">
          {/* Calendar */}
          <div className="xl:col-span-3">
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-3 sm:p-4 lg:p-6">
              {/* Calendar Header */}
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <button
                  onClick={() => navigateMonth(-1)}
                  className="p-1 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ChevronLeftIcon className="w-5 h-5 sm:w-6 sm:h-6 text-apollo-primary" />
                </button>
                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-apollo-primary text-center">
                  <span className="hidden sm:inline">{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</span>
                  <span className="sm:hidden">{monthNames[currentDate.getMonth()].slice(0, 3)} {currentDate.getFullYear()}</span>
                </h2>
                <button
                  onClick={() => navigateMonth(1)}
                  className="p-1 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ChevronRightIcon className="w-5 h-5 sm:w-6 sm:h-6 text-apollo-primary" />
                </button>
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1 sm:gap-2">
                {/* Day Headers */}
                {dayNames.map(day => (
                  <div key={day} className="text-center font-semibold text-gray-600 p-1 sm:p-2 text-xs sm:text-sm">
                    <span className="hidden sm:inline">{day}</span>
                    <span className="sm:hidden">{day.slice(0, 1)}</span>
                  </div>
                ))}

                {/* Empty cells for first week */}
                {Array.from({ length: monthStats.firstDay }, (_, i) => (
                  <div key={`empty-${i}`} className="h-12 sm:h-16 lg:h-20"></div>
                ))}

                {/* Calendar Days */}
                {attendanceData.map((dayData) => (
                  <div
                    key={dayData.date}
                    className={`h-12 sm:h-16 lg:h-20 p-1 sm:p-2 border-2 rounded-md sm:rounded-lg cursor-pointer hover:shadow-md transition-all duration-200 ${getStatusColor(dayData.status)}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs sm:text-sm font-semibold">{dayData.date}</span>
                      <div className="hidden sm:block">
                        {dayData.workType && getWorkTypeIcon(dayData.workType)}
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-center mb-1">
                      <div className="scale-75 sm:scale-100">
                        {getStatusIcon(dayData.status)}
                      </div>
                    </div>
                    
                    {dayData.hours > 0 && (
                      <div className="text-xs text-center font-medium hidden sm:block">
                        {dayData.hours}h
                      </div>
                    )}
                    
                    {dayData.checkIn && (
                      <div className="text-xs text-center opacity-75 hidden lg:block">
                        {dayData.checkIn.split(' ')[0]}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Legend */}
              <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200">
                <h4 className="text-xs sm:text-sm font-semibold text-gray-700 mb-2 sm:mb-3">Legend</h4>
                <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-4">
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <div className="w-3 h-3 sm:w-4 sm:h-4 bg-green-100 border border-green-200 rounded flex-shrink-0"></div>
                    <span className="text-xs text-gray-600">Present</span>
                  </div>
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <div className="w-3 h-3 sm:w-4 sm:h-4 bg-yellow-100 border border-yellow-200 rounded flex-shrink-0"></div>
                    <span className="text-xs text-gray-600">Late</span>
                  </div>
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <div className="w-3 h-3 sm:w-4 sm:h-4 bg-orange-100 border border-orange-200 rounded flex-shrink-0"></div>
                    <span className="text-xs text-gray-600">Half Day</span>
                  </div>
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <div className="w-3 h-3 sm:w-4 sm:h-4 bg-red-100 border border-red-200 rounded flex-shrink-0"></div>
                    <span className="text-xs text-gray-600">Absent</span>
                  </div>
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <div className="w-3 h-3 sm:w-4 sm:h-4 bg-blue-100 border border-blue-200 rounded flex-shrink-0"></div>
                    <span className="text-xs text-gray-600">Leave</span>
                  </div>
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <div className="w-3 h-3 sm:w-4 sm:h-4 bg-purple-100 border border-purple-200 rounded flex-shrink-0"></div>
                    <span className="text-xs text-gray-600">Holiday</span>
                  </div>
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <div className="w-3 h-3 sm:w-4 sm:h-4 bg-gray-100 border border-gray-200 rounded flex-shrink-0"></div>
                    <span className="text-xs text-gray-600">Weekend</span>
                  </div>
                </div>
                <div className="mt-2 sm:mt-3 grid grid-cols-3 sm:flex sm:flex-wrap gap-2 sm:gap-4">
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <BuildingOfficeIcon className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 flex-shrink-0" />
                    <span className="text-xs text-gray-600">Office</span>
                  </div>
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <HomeIcon className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 flex-shrink-0" />
                    <span className="text-xs text-gray-600">WFH</span>
                  </div>
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <div className="w-3 h-3 sm:w-4 sm:h-4 bg-purple-600 rounded-full flex-shrink-0"></div>
                    <span className="text-xs text-gray-600">Hybrid</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar Stats */}
          <div className="space-y-4 sm:space-y-6">
            {/* Monthly Summary */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4">Monthly Summary</h3>
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm sm:text-base text-gray-600">Total Days</span>
                  <span className="font-semibold text-apollo-primary text-sm sm:text-base">{monthStats.totalDays}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm sm:text-base text-gray-600">Working Days</span>
                  <span className="font-semibold text-apollo-primary text-sm sm:text-base">{monthStats.workingDays}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm sm:text-base text-gray-600">Present Days</span>
                  <span className="font-semibold text-green-600 text-sm sm:text-base">{monthStats.presentDays}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm sm:text-base text-gray-600">Absent/Leave</span>
                  <span className="font-semibold text-red-600 text-sm sm:text-base">{monthStats.absentDays}</span>
                </div>
                <div className="border-t pt-3 sm:pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm sm:text-base text-gray-600">Attendance Rate</span>
                    <span className="font-bold text-apollo-primary text-base sm:text-lg">{monthStats.attendance}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-apollo-accent h-2 rounded-full transition-all duration-500"
                      style={{ width: `${monthStats.attendance}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Hours Summary */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4">Hours Summary</h3>
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm sm:text-base text-gray-600">Total Hours</span>
                  <span className="font-semibold text-apollo-primary text-sm sm:text-base">{monthStats.totalHours}h</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm sm:text-base text-gray-600">Average/Day</span>
                  <span className="font-semibold text-apollo-primary text-sm sm:text-base">{monthStats.averageHours}h</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm sm:text-base text-gray-600">Expected</span>
                  <span className="font-semibold text-gray-600 text-sm sm:text-base">{monthStats.workingDays * 8}h</span>
                </div>
                <div className="border-t pt-3 sm:pt-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm sm:text-base text-gray-600">Efficiency</span>
                    <span className={`font-bold text-base sm:text-lg ${
                      monthStats.totalHours >= (monthStats.workingDays * 8) ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {Math.round((monthStats.totalHours / (monthStats.workingDays * 8)) * 100)}%
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4">Quick Actions</h3>
              <div className="space-y-2 sm:space-y-3">
                <button 
                  onClick={() => navigate('/attendance')}
                  className="w-full bg-gradient-to-r from-apollo-accent to-blue-600 text-white py-2 sm:py-3 px-3 sm:px-4 rounded-lg sm:rounded-xl hover:from-apollo-accent/90 hover:to-blue-600/90 transition-colors font-medium text-xs sm:text-sm"
                >
                  View Dashboard
                </button>
                <button className="w-full border border-apollo-accent text-apollo-accent py-2 sm:py-3 px-3 sm:px-4 rounded-lg sm:rounded-xl hover:bg-apollo-accent/5 transition-colors font-medium text-xs sm:text-sm">
                  Export Calendar
                </button>
                <button className="w-full border border-gray-300 text-gray-700 py-2 sm:py-3 px-3 sm:px-4 rounded-lg sm:rounded-xl hover:bg-gray-50 transition-colors font-medium text-xs sm:text-sm">
                  Print Report
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MonthlyView;
